import { useGameStore } from './store';
import { createAudioEngine } from '@org/audio-engine';
import type { SoundDef } from '@org/audio-engine';

const base = import.meta.env.BASE_URL;

type NonogramSound =
  | 'fill'
  | 'mark'
  | 'undo'
  | 'line-complete'
  | 'error'
  | 'win'
  | 'click'
  | 'navigate'
  | 'toggle'
  | 'mode-switch'
  | 'tutorial-wrong'
  | 'step-complete'
  | 'popup'
  | 'page-turn';

const sounds: Record<NonogramSound, SoundDef> = {
  fill: [{ kind: 'tone', freq: 600, dur: 0.1, vol: 0.15 }],
  mark: [{ kind: 'tone', freq: 300, dur: 0.08, vol: 0.12, wave: 'triangle' }],
  undo: [{ kind: 'tone', freq: 400, dur: 0.06, vol: 0.1 }],
  'line-complete': [{ kind: 'tone', freq: 800, dur: 0.15, vol: 0.1 }],
  error: [{ kind: 'tone', freq: 200, dur: 0.15, vol: 0.18, wave: 'triangle' }],
  win: [
    { kind: 'tone', freq: 523, dur: 0.3, vol: 0.15 },
    { kind: 'tone', freq: 659, dur: 0.3, vol: 0.15, delay: 0.12 },
    { kind: 'tone', freq: 784, dur: 0.3, vol: 0.15, delay: 0.24 },
    { kind: 'tone', freq: 1047, dur: 0.3, vol: 0.15, delay: 0.36 },
  ],
  click: [
    { kind: 'tone', freq: 900, dur: 0.04, vol: 0.08 },
    { kind: 'noise', dur: 0.02, vol: 0.04 },
  ],
  navigate: (ctx) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(300, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.12);
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
  },
  toggle: [{ kind: 'tone', freq: 700, dur: 0.03, vol: 0.1, wave: 'square' }],
  'mode-switch': [
    { kind: 'tone', freq: 500, dur: 0.05, vol: 0.08 },
    { kind: 'tone', freq: 700, dur: 0.05, vol: 0.08, delay: 0.05 },
  ],
  'tutorial-wrong': [
    { kind: 'tone', freq: 150, dur: 0.12, vol: 0.12, wave: 'sawtooth' },
    { kind: 'tone', freq: 130, dur: 0.12, vol: 0.1, wave: 'sawtooth', delay: 0.06 },
  ],
  'step-complete': [
    { kind: 'tone', freq: 660, dur: 0.12, vol: 0.1 },
    { kind: 'tone', freq: 880, dur: 0.15, vol: 0.1, delay: 0.1 },
  ],
  popup: [
    { kind: 'tone', freq: 500, dur: 0.06, vol: 0.07 },
    { kind: 'tone', freq: 800, dur: 0.08, vol: 0.07, delay: 0.04 },
  ],
  'page-turn': [
    { kind: 'noise', dur: 0.04, vol: 0.06 },
    { kind: 'tone', freq: 600, dur: 0.04, vol: 0.05, delay: 0.01 },
  ],
};

const audio = createAudioEngine({
  tracks: [
    `${base}audio/cat-caffe.m4a`,
    `${base}audio/florist.m4a`,
    `${base}audio/bartender.m4a`,
  ],
  sounds,
  musicVolume: 0.18,
  isSfxEnabled: () => useGameStore.getState().soundEnabled,
  isMusicEnabled: () => useGameStore.getState().musicEnabled,
});

export const {
  playSound,
  ensureAudioContext,
  startMusic,
  onMusicToggle,
} = audio;

// Backward-compatible named exports used by existing components
export const playFill = () => audio.playSound('fill');
export const playMark = () => audio.playSound('mark');
export const playUndo = () => audio.playSound('undo');
export const playLineComplete = () => audio.playSound('line-complete');
export const playError = () => audio.playSound('error');
export const playWin = () => audio.playSound('win');
export const playClick = () => audio.playSound('click');
export const playNavigate = () => audio.playSound('navigate');
export const playToggle = () => audio.playSound('toggle');
export const playModeSwitch = () => audio.playSound('mode-switch');
export const playTutorialWrong = () => audio.playSound('tutorial-wrong');
export const playStepComplete = () => audio.playSound('step-complete');
export const playPopup = () => audio.playSound('popup');
export const playPageTurn = () => audio.playSound('page-turn');
